import React, { useState } from 'react';
import Ticket from './Ticket';
import { TicketStatus, GroupedTicket as GroupedTicketType, Ticket as SubTicketType, ForSaleTicket, SoldTicket } from '@/lib/OrderApi';
import { MoneyValues } from '@/lib/TicketApi';
import { Dialog } from '../shared/Dialog';
import ListTicketModal from './listTicketModal';
import { RiCashLine, RiStore2Line, RiErrorWarningLine } from '@remixicon/react';
import { Money } from '@/lib/money';
import { useLanguage } from '@/providers/LanguageProvider';
import SoldDetailsModal from './SoldDetailsModal';
import * as m from "@/paraglide/messages.js"

// Define the type for sold tickets needed for the modal
type SoldTicketWithDetails = SoldTicket & Partial<Pick<ForSaleTicket, 'listingPriceExFee' | 'resaleFeeAmountSeller'>> & { 
    sellerReceivable?: MoneyValues 
};

interface GroupedTicketProps {
  orderId: string;
  ticket: GroupedTicketType;
  answers: Record<string, object[] | string[]>;
  triggeredReload: () => void;
  marketplaceUrl: string | null;
  eventName: string;
}

const GroupedTicket: React.FC<GroupedTicketProps> = ({ orderId, ticket, answers, triggeredReload, marketplaceUrl, eventName }) => {
  const [listTicketModalOpen, setListTicketModalOpen] = useState(false);
  const [soldDetailsModalOpen, setSoldDetailsModalOpen] = useState(false);
  const { currentLanguage: lang } = useLanguage();

  // Determine statuses
  const showSellButton = (ticket.ticketStatus === TicketStatus.available || ticket.ticketStatus === TicketStatus.claimable) 
    && (ticket.canResell || !!ticket.sealedSecureSwapUrl);
  const isForSale = ticket.ticketStatus === TicketStatus.forSale;
  const isSold = ticket.ticketStatus === TicketStatus.sold;

  // Cast to access potential properties based on status
  const ticketForSale = isForSale ? ticket as GroupedTicketType & ForSaleTicket : null;
  // Cast the main group ticket if sold for the modal
  const soldTicketDetails = isSold ? ticket as SoldTicketWithDetails : null; 

  return (
    <div key={ticket.ticketId} className="relative text-[var(--shop-textColor)] ">
        <div className='bg-[var(--shop-panelBackgroundColor)] rounded-md p-4 mb-4'>
            {/* Conditional Sell / For Sale / Sold Display */}
            {showSellButton && (
                <button
                    onClick={() => setListTicketModalOpen(true)}
                    className='absolute top-4 right-4 flex items-center hover:underline hover:cursor-pointer hover:bg-[var(--shop-panelBackgroundColor)] rounded-md p-2 -m-2'
                >
                    <RiCashLine className='w-6 h-6' />
                    <span className='ml-2 tracking-tight font-semibold'>{m.marketplace_sell()}</span>
                </button>
            )}
            {ticketForSale && (
                 <button
                    onClick={() => setListTicketModalOpen(true)}
                    className='absolute top-4 right-4 flex items-center hover:underline hover:cursor-pointer hover:bg-[var(--shop-panelBackgroundColor)] rounded-md p-2 -m-2 '
                >
                    <RiStore2Line className='w-6 h-6' />
                    <span className='ml-2 tracking-tight font-semibold'>{m.marketplace_listed_on_marketplace()}</span>
                </button>
            )}
            {soldTicketDetails && (
                 <button
                    onClick={() => setSoldDetailsModalOpen(true)}
                    className='absolute top-4 right-4 flex flex-col items-end hover:cursor-pointer hover:bg-[var(--shop-panelBackgroundColor)] rounded-md p-2 -m-2 text-right'
                 >
                    <div className='flex items-center'>
                        <RiStore2Line className='w-6 h-6' />
                        <span className='ml-2 tracking-tight font-semibold'>{m.marketplace_sold_on_marketplace()}</span>
                    </div>
                    {soldTicketDetails.sellerReceivable && (
                        <span className='text-sm mt-1'>
                            ({m.marketplace_receive()}: {Money.fromInteger(soldTicketDetails.sellerReceivable.amount, soldTicketDetails.sellerReceivable.currency).display(lang)})
                        </span>
                    )}
                 </button>
            )}
            <h3 className={`text-lg font-semibold mb-1 ${(showSellButton || ticketForSale || soldTicketDetails) ? 'pr-40' : ''}`}>{ticket.productName}</h3>
            <p className="text-sm mb-1">Group Ticket (Contains {ticket.tickets.length} items)</p>
            {/* Display Group Ticket Price */}
            <p className="text-sm font-semibold mb-2">
              Total Price: {Money.fromInteger(ticket.price.amount, ticket.price.currency).add(Money.fromInteger(ticket.serviceFee.amount, ticket.serviceFee.currency)).display(lang)}
            </p>
            {ticket.canResell && (
              <p className='text-sm mb-2'>
                  Group tickets can only be resold in their entirety.
              </p>
            )}
            {/* Warning for For Sale status */}
            {ticketForSale && (
                <div className="text-xs text-orange-600 bg-orange-100 border border-orange-300 rounded-md mt-2 p-2 flex items-center">
                    <RiErrorWarningLine size={16} className="mr-2 flex-shrink-0" />
                    <span>{m.marketplace_group_ticket_listed_warning()}</span>
                </div>
            )}
            {/* Added Warning for Sold status */}
            {soldTicketDetails && (
                <div className="text-xs text-green-700 bg-green-100 border border-green-300 rounded-md mt-2 p-2 flex items-center">
                    <RiErrorWarningLine size={16} className="mr-2 flex-shrink-0" />
                    <span>This group ticket has been sold. The individual tickets below are no longer valid for entry or use.</span>
                </div>
            )}
        </div>
      
      {/* Render Sub-Tickets with left border, adjust padding/margin */}
      <div className="mt-4 pl-4 border-l-2 space-y-0" style={{ borderColor: 'var(--shop-borderColor)' }}>
        {ticket.tickets.map((subTicket: SubTicketType) => (
          <div key={subTicket.ticketId} >
            <Ticket
              orderId={orderId}
              ticket={subTicket}
              answers={answers}
              triggeredReload={triggeredReload}
              marketplaceUrl={marketplaceUrl}
              eventName={eventName}
              isSubTicket={true}
            />
          </div>
        ))}
      </div>

      {/* Sell Modal */}
      <Dialog open={listTicketModalOpen} onOpenChange={setListTicketModalOpen}>
        <ListTicketModal 
          ticket={ticket}
          marketplaceUrl={marketplaceUrl}
          onSuccess={() => {
            triggeredReload();
            setListTicketModalOpen(false);
          }} 
          orderId={orderId}
        />
      </Dialog>
      
      {/* Sold Details Modal for the Group Ticket */}
      <SoldDetailsModal 
        isOpen={soldDetailsModalOpen} 
        onClose={() => setSoldDetailsModalOpen(false)} 
        ticket={soldTicketDetails}
      />
    </div>
  );
};

export default GroupedTicket; 